import { SocialProvider } from '@/__generated__/graphql';
import type { SocialSignup } from '@/auth';

// ENG-6169: "Continue with Google" or "Continue with Microsoft" on the login
// page found no account. Its answer reaches the signup form the person picks
// on the login card (ENG-6311), so the form starts as that provider's sign-up:
// prefilled, no password.
//
// The tokens stay in this module's memory; router state carries only a
// reference. History entries outlive the flow (Back, reload, session restore),
// and the provider's ID token signs its owner in for as long as it lives. A
// reload or another tab resolves nothing and gets the plain form. An entry
// ends with the signup token's 30 minutes, a completed signup, or logout.
// The provider's name rides beside the reference, so a form reached after the
// entry is gone can say whose sign-up expired (ENG-6311).
export interface SocialSignupHandoff {
  social: SocialSignup;
  email: string;
  firstName: string;
  lastName: string;
}

const SIGNUP_TOKEN_TTL_MS = 30 * 60 * 1000;

const handoffs = new Map<string, { handoff: SocialSignupHandoff; expiresAt: number }>();

/** Router state for the links that carry a social sign-up to a signup form. */
export function socialSignupState(handoff: SocialSignupHandoff): {
  socialSignup: string;
  socialProvider: SocialProvider;
} {
  const ref = crypto.randomUUID();
  handoffs.set(ref, { handoff, expiresAt: Date.now() + SIGNUP_TOKEN_TTL_MS });
  return { socialSignup: ref, socialProvider: handoff.social.provider };
}

function referenceIn(state: unknown): string | undefined {
  if (typeof state !== 'object' || state === null) return undefined;
  const ref = (state as Record<string, unknown>).socialSignup;
  return typeof ref === 'string' ? ref : undefined;
}

export function readSocialSignupHandoff(state: unknown): SocialSignupHandoff | undefined {
  const ref = referenceIn(state);
  if (!ref) return undefined;
  const entry = handoffs.get(ref);
  if (!entry) return undefined;
  if (Date.now() >= entry.expiresAt) {
    handoffs.delete(ref);
    return undefined;
  }
  return entry.handoff;
}

/** The provider of a sign-up router state still refers to but this page no longer holds. */
export function expiredSocialSignupProvider(state: unknown): SocialProvider | undefined {
  if (!referenceIn(state) || readSocialSignupHandoff(state)) return undefined;
  const provider = (state as Record<string, unknown>).socialProvider;
  return Object.values(SocialProvider).find((known) => known === provider);
}

/** Forgets the social sign-up router state refers to: the person chose another account. */
export function forgetSocialSignup(state: unknown): void {
  const ref = referenceIn(state);
  if (ref) handoffs.delete(ref);
}

/** Forgets every social sign-up: once a signup is created, and on logout. */
export function clearSocialSignupHandoffs(): void {
  handoffs.clear();
}
